import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    DatePicker,
    NumberInput,
    Autocomplete,
    AutocompleteItem,
} from "@heroui/react";
import { useEffect } from "react";
import { usePostData, useFetchData } from "@/hooks";
import { useForm, Controller } from "react-hook-form";
import { findOptionTitle, showToast } from "@/utils";
import { parseDate } from "@internationalized/date";
import { ThemedText } from "@/components/ui";

const StartBatchFormModal = ({
    isOpen,
    onClose,
    barrackId = null,
    onSuccess,
}) => {
    const defaultValues = {
        date: null,
        population_in: null,
        age: null,
        material_id: null,
    };

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues,
    });

    const mutation = usePostData(`barracks/${barrackId}/start_batch`);
    const { data: materials, isLoading: isLoadingMaterials } = useFetchData(
        "select/egg-materials",
    );

    useEffect(() => {
        if (isOpen) {
            reset(defaultValues);
        }
    }, [isOpen, reset]);

    const handleClose = () => {
        reset(defaultValues);
        onClose();
    };

    const onSubmit = (data) => {
        if (!barrackId) {
            showToast("error", "Error", "Barrack ID missing");
            return;
        }

        mutation.mutate(data, {
            onSuccess: (response) => {
                showToast(
                    "success",
                    "Batch baru dimulai!",
                    response?.data?.message || "Batch baru berhasil dimulai!",
                );
                onSuccess?.();
                handleClose();
            },
            onError: (err) => {
                console.error("Failed to start batch:", err);
                const msg =
                    err.response?.data?.message ||
                    "Terjadi kesalahan pada sistem. Mohon coba beberapa saat lagi.";
                showToast("error", "Gagal Menyimpan", msg);
            },
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && handleClose()}
            placement="center"
        >
            <ModalContent>
                {() => (
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <ModalHeader className="flex flex-col gap-1">
                            Populasi Baru
                        </ModalHeader>
                        <ModalBody>
                            <Controller
                                name="material_id"
                                control={control}
                                rules={{
                                    required: "Jenis Telur wajib dipilih",
                                }}
                                render={({ field }) => (
                                    <Autocomplete
                                        label="Jenis Telur Produksi"
                                        placeholder="Pilih jenis telur"
                                        variant="bordered"
                                        defaultItems={materials || []}
                                        isLoading={isLoadingMaterials}
                                        selectedKey={
                                            field.value
                                                ? String(field.value)
                                                : null
                                        }
                                        onSelectionChange={(key) =>
                                            field.onChange(key)
                                        }
                                        isInvalid={!!errors.material_id}
                                        errorMessage={
                                            errors.material_id?.message
                                        }
                                        isRequired
                                    >
                                        {(item) => (
                                            <AutocompleteItem key={item.value}>
                                                {item.title}
                                            </AutocompleteItem>
                                        )}
                                    </Autocomplete>
                                )}
                            />

                            <Controller
                                name="date"
                                control={control}
                                rules={{
                                    required: "Tanggal masuk wajib diisi",
                                }}
                                render={({ field }) => (
                                    <DatePicker
                                        label="Tanggal Masuk Ayam"
                                        variant="bordered"
                                        isRequired
                                        showMonthAndYearPickers
                                        isInvalid={!!errors.date}
                                        color={
                                            errors.date ? "danger" : "default"
                                        }
                                        errorMessage={errors.date?.message}
                                        value={
                                            field.value
                                                ? parseDate(field.value)
                                                : null
                                        }
                                        onChange={(date) =>
                                            field.onChange(
                                                date ? date.toString() : "",
                                            )
                                        }
                                    />
                                )}
                            />

                            <Controller
                                name="population_in"
                                control={control}
                                rules={{
                                    required: "Jumlah ayam wajib diisi",
                                    min: {
                                        value: 1,
                                        message: "Minimal 1 ekor",
                                    },
                                }}
                                render={({ field }) => (
                                    <NumberInput
                                        label="Jumlah Ayam Masuk"
                                        placeholder="Contoh: 100"
                                        variant="bordered"
                                        hideStepper
                                        minValue={1}
                                        isRequired
                                        isClearable
                                        isInvalid={!!errors.population_in}
                                        errorMessage={
                                            errors.population_in?.message
                                        }
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        isWheelDisabled
                                    />
                                )}
                            />

                            <Controller
                                name="age"
                                control={control}
                                rules={{
                                    required: "Umur ayam wajib diisi",
                                    min: {
                                        value: 0,
                                        message: "Umur tidak valid",
                                    },
                                }}
                                render={({ field }) => (
                                    <NumberInput
                                        label="Umur Ayam"
                                        placeholder="Contoh: 100"
                                        variant="bordered"
                                        hideStepper
                                        isClearable
                                        endContent={
                                            <ThemedText
                                                size="sm"
                                                variant="caption"
                                            >
                                                Hari
                                            </ThemedText>
                                        }
                                        minValue={1}
                                        isRequired
                                        isInvalid={!!errors.age}
                                        errorMessage={errors.age?.message}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        isWheelDisabled
                                    />
                                )}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="danger"
                                variant="flat"
                                onPress={handleClose}
                                size="sm"
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                isLoading={mutation.isLoading}
                                size="sm"
                            >
                                Mulai Batch
                            </Button>
                        </ModalFooter>
                    </form>
                )}
            </ModalContent>
        </Modal>
    );
};

export default StartBatchFormModal;
